import { formatDollars, lowercaseFirst, withArticle } from '@fruitful/fruitils';
import { MoneyMapEmbeddedVisual } from '@fruitful/money-map';
import {
    type OnboardingChatResultsTooltipData,
    OnboardingChatResultsTooltipType,
    ResultSlide,
    type RuleCompletionInfo,
    RuleSetType,
    RuleType,
} from '@fruitful/types';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
    BodyLargeEmphasized,
    BodySmallRegular,
    CirclePlay,
    H6Regular,
    Icon,
    LabelRegular,
    Shadows,
    Slider,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { ConversationalMMAnalyticsEvent } from '../analytics/events';
import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import useAdvanceTooltip from '../hooks/useAdvanceTooltip';
import { useGetRuleSets } from '../hooks/useGetRuleSets';
import { GuidedTooltip } from './GuidedTooltip';
import { RuleSetCard } from './RuleSetCard';
import { Header, LinearGradientBackgroundCard } from './shared';
import { SlideNextButton } from './SlideNextButton';

const SpendGoalsSlider = ({
    spendCents,
    goalsCents,
    spendPercent,
    goalsPercent,
    remainingCents,
    onSlideMove,
    onSlideEnd,
}: {
    spendCents: number;
    goalsCents: number;
    spendPercent: number;
    goalsPercent: number;
    remainingCents: number;
    onSlideMove: (value: number) => void;
    onSlideEnd: (value: number) => void;
}) => {
    const stepCents = useMemo(() => {
        return remainingCents < 200000 ? 1000 : 2000;
    }, [remainingCents]);

    const roundToStep = useCallback(
        (value: number) => {
            return Math.round(value / stepCents) * stepCents;
        },
        [stepCents],
    );

    const sliderValue = useMemo(() => [spendCents], [spendCents]);

    return (
        <YStack
            width="100%"
            padding={16}
            backgroundColor="$white"
            borderRadius={16}
            gap={12}
            {...Shadows.SMALL}
        >
            <XStack width="100%" justifyContent="space-between">
                <XStack gap={6} alignItems="center">
                    <View
                        width={8}
                        height={8}
                        borderRadius={4}
                        backgroundColor="$leaf"
                    />
                    <LabelRegular>Spend</LabelRegular>
                </XStack>
                <XStack gap={6} alignItems="center">
                    <LabelRegular>Goals</LabelRegular>
                    <View
                        width={8}
                        height={8}
                        borderRadius={4}
                        backgroundColor="$petal"
                    />
                </XStack>
            </XStack>

            <Slider
                value={sliderValue}
                onSlideMove={(_, slideValue: number) => {
                    onSlideMove(roundToStep(slideValue));
                }}
                onSlideEnd={() => {
                    onSlideEnd(roundToStep(spendCents));
                }}
                min={stepCents}
                max={remainingCents - stepCents}
                step={stepCents}
                width="100%"
                trackActiveColor="$green800"
                trackColor="$orchidPink600"
                thumbSize={24}
            />

            <XStack width="100%">
                <YStack gap={2} flex={1}>
                    <BodyLargeEmphasized>
                        {formatDollars(spendCents / 100, false)}
                    </BodyLargeEmphasized>
                    <LabelRegular color="$gray1000">
                        {spendPercent}% of income
                    </LabelRegular>
                </YStack>
                <YStack gap={2} flex={1} alignItems="flex-end">
                    <BodyLargeEmphasized>
                        {formatDollars(goalsCents / 100, false)}
                    </BodyLargeEmphasized>
                    <LabelRegular color="$gray1000">
                        {goalsPercent}% of income
                    </LabelRegular>
                </YStack>
            </XStack>
        </YStack>
    );
};

const RuleCards = () => {
    const { setResults } = useConversationalMMContext();
    const { ruleSetDisplays, hideDates, emergencyFundUnreachable } =
        useGetRuleSets();

    useEffect(() => {
        if (ruleSetDisplays == null) return;
        setResults((prev) => ({
            ...prev,
            goalCompletionDates: {
                ...prev.goalCompletionDates,
                [ResultSlide.Goals]: ruleSetDisplays,
            },
        }));
    }, [ruleSetDisplays, setResults]);

    if (ruleSetDisplays == null) {
        return null;
    }

    return (
        <YStack gap={12} width="100%">
            {ruleSetDisplays.map((ruleSet) => (
                <RuleSetCard
                    key={ruleSet.name}
                    ruleSet={ruleSet}
                    hideDates={hideDates}
                    hideDatesForAllocationType={
                        !hideDates && emergencyFundUnreachable
                            ? 'MINIMUM_BALANCE'
                            : undefined
                    }
                />
            ))}
            {!hideDates && (
                <BodySmallRegular color="$black" textAlign="center">
                    All dates are estimated
                </BodySmallRegular>
            )}
        </YStack>
    );
};

const MoneyMapSlide = () => {
    const {
        results,
        setResults,
        tooltipSequence,
        setTooltipSequence,
        setTooltipData,
        activeTooltip,
        setActiveTooltip,
        showGuidedTooltips,
        hasSeenGuidedTooltips,
        onGuidedTooltipsSeen,
        onTrackEvent,
    } = useConversationalMMContext();
    const { moneyMap, rawChatResults } = results;
    const {
        incomeTotalCents,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    } = moneyMap;

    const noGoalsMoney = useMemo(() => goalsTotalCents <= 0, [goalsTotalCents]);

    const remainingCents = useMemo(
        () => incomeTotalCents - billsTotalCents,
        [incomeTotalCents, billsTotalCents],
    );

    const updateSpendGoalsValues = (spendCents: number) => {
        setResults({
            ...results,
            moneyMap: {
                ...moneyMap,
                spendTotalCents: spendCents,
                goalsTotalCents: remainingCents - spendCents,
            },
        });
    };

    const handleSpendGoalsSlideEnd = (spendCents: number) => {
        updateSpendGoalsValues(spendCents);
        onTrackEvent(ConversationalMMAnalyticsEvent.SpendGoalsSlider, {
            spendPercentage: spendCents / remainingCents,
        });
    };

    const spendPercent = Math.round((spendTotalCents / incomeTotalCents) * 100);
    const goalsPercent = Math.round((goalsTotalCents / incomeTotalCents) * 100);

    const stepCents = remainingCents < 200000 ? 1000 : 2000;
    const sliderKnobPercent = useMemo(() => {
        const sliderMin = stepCents;
        const sliderMax = remainingCents - stepCents;
        const range = sliderMax - sliderMin;
        if (range <= 0) return 0.5;
        return (spendTotalCents - sliderMin) / range;
    }, [spendTotalCents, remainingCents, stepCents]);

    const { ruleSetDisplays } = useGetRuleSets();

    useEffect(() => {
        if (!showGuidedTooltips) return;

        const sequence: OnboardingChatResultsTooltipType[] = [
            OnboardingChatResultsTooltipType.MoneyMap,
        ];
        const data = {};

        if (!noGoalsMoney) {
            sequence.push(OnboardingChatResultsTooltipType.SpendGoalsSlider);
        }

        let debtRule: RuleCompletionInfo | null = null;

        for (const ruleSet of ruleSetDisplays ?? []) {
            if (ruleSet.type === RuleSetType.FinancialHealth) {
                sequence.push(
                    OnboardingChatResultsTooltipType.FinancialHealthSet,
                );
                data[OnboardingChatResultsTooltipType.FinancialHealthSet] = {
                    title: 'Financial Health goals',
                    description:
                        "After building a 1 month emergency fund, you'll focus on financial health goals.",
                };
            }

            for (const rule of ruleSet.rules) {
                if (rule.type === RuleType.StarterEmergencyFund) {
                    sequence.push(
                        OnboardingChatResultsTooltipType.StarterEmergencyFund,
                    );
                    data[
                        OnboardingChatResultsTooltipType.StarterEmergencyFund
                    ] = {
                        title: `Goal: ${rule.description}`,
                        description:
                            'Your first goal will be to start an emergency fund to cover 1 month of expenses',
                    };
                }
                if (rule.type === RuleType.DebtPayDown) {
                    sequence.push(OnboardingChatResultsTooltipType.DebtPayDown);
                    data[OnboardingChatResultsTooltipType.DebtPayDown] = {
                        title: `Goal: ${rule.description}`,
                        description: `Your primary goal will be to ${lowercaseFirst(rule.description)}, while contributing to your emergency fund and retirement`,
                    };
                    debtRule = rule;
                }
                if (rule.type === RuleType.Savings) {
                    const MAX_CENTS = 20_000_000;
                    const savGoals = rawChatResults?.savings ?? [];
                    const savUnder = savGoals.filter(
                        (g) => g.targetAmountCents <= MAX_CENTS,
                    );
                    const savPrimary =
                        savUnder.length > 0
                            ? savUnder.reduce((best, g) =>
                                  g.targetAmountCents > best.targetAmountCents
                                      ? g
                                      : best,
                              )
                            : savGoals.length > 0
                              ? savGoals.reduce((best, g) =>
                                    g.targetAmountCents < best.targetAmountCents
                                        ? g
                                        : best,
                                )
                              : undefined;
                    const goalName = savPrimary?.description;
                    const goalPhrase =
                        goalName != null && goalName !== ''
                            ? `save for ${withArticle(goalName)}`
                            : 'reach your goals';

                    sequence.push(OnboardingChatResultsTooltipType.Savings);
                    data[OnboardingChatResultsTooltipType.Savings] = {
                        title: `Goal: ${rule.description}`,
                        description: sequence.includes(
                            OnboardingChatResultsTooltipType.DebtPayDown,
                        )
                            ? `You will ${goalPhrase} while you ${debtRule?.description != null ? lowercaseFirst(debtRule.description) : ''}`
                            : `Your primary goal will be to ${goalPhrase}, while contributing to your emergency fund and retirement`,
                    };
                }
            }
        }

        setTooltipSequence(sequence);
        setTooltipData(data as OnboardingChatResultsTooltipData);
    }, [showGuidedTooltips, noGoalsMoney, ruleSetDisplays]);

    useEffect(() => {
        if (!showGuidedTooltips || hasSeenGuidedTooltips) return;
        const timer = setTimeout(() => {
            setActiveTooltip(tooltipSequence[0] ?? null);
        }, 800);
        return () => {
            clearTimeout(timer);
        };
    }, [showGuidedTooltips, hasSeenGuidedTooltips, tooltipSequence]);

    const markTooltipsSeen = useCallback(() => {
        onGuidedTooltipsSeen();
    }, [onGuidedTooltipsSeen]);

    const advanceTooltip = useAdvanceTooltip();

    return (
        <LinearGradientBackgroundCard>
            <YStack
                gap={24}
                width="100%"
                justifyContent="center"
                alignItems="center"
            >
                <YStack width="100%" gap={8}>
                    <Header
                        title="Your Money Map"
                        description="Based on everything you shared, here's your system we built together"
                    />
                    <XStack
                        gap={8}
                        alignItems="center"
                        cursor="pointer"
                        alignSelf="flex-start"
                        onPress={() => {
                            setActiveTooltip(
                                OnboardingChatResultsTooltipType.MoneyMap,
                            );
                        }}
                    >
                        <BodySmallRegular color="$gray1000">
                            Play walkthrough
                        </BodySmallRegular>
                        <Icon icon={CirclePlay} size={16} color="$gray1000" />
                    </XStack>
                </YStack>
                <YStack
                    width="100%"
                    alignItems="center"
                    justifyContent="center"
                    gap={12}
                >
                    <GuidedTooltip
                        title="Income"
                        content={`Your ${formatDollars(incomeTotalCents / 100, false)} monthly take-home pay is routed through Income Split to separate accounts for your monthly expenses and then to your Goals`}
                        isActive={
                            activeTooltip ===
                            OnboardingChatResultsTooltipType.MoneyMap
                        }
                        onNext={advanceTooltip}
                        isLast={
                            tooltipSequence[tooltipSequence.length - 1] ===
                            OnboardingChatResultsTooltipType.MoneyMap
                        }
                        verticalOffset={360}
                        onBecameVisible={markTooltipsSeen}
                        autoScroll={false}
                        delay={hasSeenGuidedTooltips ? 0 : 1000}
                    >
                        <YStack width="100%" alignItems="center" gap={24}>
                            <H6Regular>Your Money Map</H6Regular>
                            <MoneyMapEmbeddedVisual
                                incomeTotalCents={incomeTotalCents}
                                billsTotalCents={billsTotalCents}
                                spendTotalCents={spendTotalCents}
                                goalsTotalCents={goalsTotalCents}
                            />
                        </YStack>
                    </GuidedTooltip>
                    {!noGoalsMoney && (
                        <GuidedTooltip
                            title="Spend + Goals"
                            content="Adjust your Spend and Goals to what feels right. You can change this later."
                            isActive={
                                activeTooltip ===
                                OnboardingChatResultsTooltipType.SpendGoalsSlider
                            }
                            onNext={advanceTooltip}
                            isLast={
                                tooltipSequence[tooltipSequence.length - 1] ===
                                OnboardingChatResultsTooltipType.SpendGoalsSlider
                            }
                            arrowPercent={sliderKnobPercent}
                            verticalOffset={24}
                            placement="top"
                        >
                            <SpendGoalsSlider
                                spendCents={spendTotalCents}
                                goalsCents={goalsTotalCents}
                                spendPercent={spendPercent}
                                goalsPercent={goalsPercent}
                                remainingCents={remainingCents}
                                onSlideMove={updateSpendGoalsValues}
                                onSlideEnd={handleSpendGoalsSlideEnd}
                            />
                        </GuidedTooltip>
                    )}
                </YStack>
                <RuleCards />
                <SlideNextButton />
            </YStack>
        </LinearGradientBackgroundCard>
    );
};

export default MoneyMapSlide;
